// @givmo/fundraisers-module — every line the requests to join show in the consoles (FCM-WEB33), as
// Jonathan ruled them with the mockups on 2026-10-17 10:41 PDT (D-FCM-35, "approved"; plan D-FCM-27),
// `scratchpad/2026-10-07-fcm-web33-join-requests-mockups/`. One change from the drawn set is his: the
// setting's new choice reads "Request to join", his own words, where the frames drew "Ask to join", and
// so does the button that asks. The six terms a campaign agrees to are FCM-WEB25's, in `layers-copy.ts`.
//
// The join setting's question, the line under it, and the three choices' labels and lines are the
// creation wizard's (EMC-WEB7), which supersede D-FCM-35's for the setting (D-EMC-19, Jonathan
// 2026-10-28: "agree"), so a person reads one description of it in the wizard and on the Campaign tab.
// "Members only" and its host's words are unchanged.
//
// The module's copy law bans the words that would say a transfer happened, so a campaign's own request
// reads "Asked to join {parent}." and "Pending" here; the donate page says what a person's request did.

import { possessive, series } from './layers-copy';

/** The campaign's ONE join setting (D-FCM-27), as the server stores it. */
export type JoinPolicyValue = 'open' | 'request_to_join' | 'invite_only' | 'members_only';

/** The order the choices read in, whatever order the server offers them (the ruled frame 01). */
export const JOIN_POLICY_ORDER: readonly JoinPolicyValue[] = [
  'open',
  'request_to_join',
  'invite_only',
  'members_only',
];

const capitalized = (words: string) => `${words.charAt(0).toUpperCase()}${words.slice(1)}`;

export const REQUESTS_COPY = {
  // ── The join setting: one setting for people's pages and other campaigns' layers ─────────────
  shutterLabel: 'Accept new fundraiser pages and campaigns',
  shutterPaused:
    'Paused. No one can join or ask to join until you turn this back on. You can still answer the requests already waiting.',
  endedLine:
    'This campaign has ended, so no one can join or ask to join. The requests that were waiting were cancelled.',
  whoCanJoin: 'Who can create a fundraiser page?',
  /** Under the question: the one setting governs other organizations' campaigns too. */
  whoCanJoinScope: 'The same applies to other organizations adding their campaigns to yours.',
  policyLabel: {
    open: 'Anyone with the link',
    request_to_join: 'People you approve',
    invite_only: 'Only people you invite',
    members_only: 'Members only',
  } satisfies Record<JoinPolicyValue, string>,
  policyDescription: {
    open: 'They start a page right away.',
    request_to_join:
      'They ask first, and you approve each one. Anyone you invite starts right away.',
    invite_only: 'Only the people you invite by email can start a page.',
  } satisfies Record<Exclude<JoinPolicyValue, 'members_only'>, string>,
  /** The roster's words are the host's data (the company's "your employees and admins"). */
  membersOnlyDescription: (people: string) =>
    `${capitalized(people)} can start a page right away. Other organizations’ campaigns become layers only when you invite them.`,
  ownCampaignsLine: 'Campaigns your organization runs can always be added on the Layers tab.',

  // ── The requests: people's on the Campaign tab, campaigns' on the Layers tab ─────────────────
  requestsTitle: 'Requests to join',
  peopleSubtitle:
    'People asking to start a page under this campaign. A request expires after 14 days if no one answers.',
  campaignsSubtitle:
    'Campaigns other organizations run, asking to become layers of this one. A request expires after 14 days if no one answers.',
  emptyTitle: 'No requests right now',
  /**
   * NOT IN THE RULED SET, for Jonathan's OK (cut `fc-join-web-1019`'s report): a list the console
   * can't read, in the module's own pattern for one ("Couldn’t load invitations").
   */
  loadFailed: 'Couldn’t load requests',
  emptyPeople: 'When someone asks to join, their request shows here and you get an email.',
  emptyCampaigns:
    'When another organization’s campaign asks to join, its request shows here and you get an email.',
  colSupporter: 'Supporter',
  colTeam: 'Team',
  colRaisesFor: 'Raises for',
  colAsked: 'Asked',
  noTeam: 'No team',
  newTeam: (name: string) => `New team: ${name}`,
  asked: (date: string) => `Asked ${date}`,
  expires: (date: string) => `Expires ${date}`,
  approve: 'Approve',
  decline: 'Decline',
  status: {
    pending: 'Pending',
    approved: 'Approved',
    declined: 'Declined',
    expired: 'Expired',
    withdrawn: 'Withdrawn',
    cancelled: 'Cancelled',
  } as Readonly<Record<string, string>>,
  decidedOn: (status: string, date: string) => `${status} ${date}`,
  /** After a cancelled request's date, why: "Cancelled Jun 30, 2026, when it ended". */
  whenThisEnded: 'when this campaign ended',
  whenItEnded: 'when it ended',
  whenItJoinedElsewhere: 'when it joined another campaign',
  earlier: 'Earlier',
  approvedToast: 'Request approved.',
  declinedToast: 'Request declined.',
  answerFailed: 'Couldn’t answer this request. Please try again.',
  declineTitle: 'Decline this request?',
  declinePerson: (name: string, campaign: string) =>
    `${name} can’t ask to join ${campaign} again, but an invitation from this campaign’s organizers can still bring them in.`,
  declineCampaign: (child: string, campaign: string) =>
    `${child} can’t ask to join ${campaign} again, but an invitation from this campaign’s organizers can still bring it in.`,
  declineConfirm: 'Decline request',
  /** The tab count's spoken name. */
  waitingCount: (count: number) =>
    count === 1 ? '1 request waiting' : `${count} requests waiting`,

  // ── A campaign's request, the parent's side ──────────────────────────────────────────────────
  runBy: (organization: string) => `Run by ${organization}`,
  fitsCampaign: (charities: readonly string[]) =>
    `Raises for ${series(charities)}, as this campaign requires.`,
  fitsList: (charities: readonly string[]) =>
    `Raises for ${series(charities)}, from this campaign’s list.`,
  noFitCampaign:
    'It raises for other charities than this campaign requires, so it can’t be approved.',
  noFitList:
    'It raises for charities that aren’t on this campaign’s list, so it can’t be approved.',
  itsLayers: (names: readonly string[], count: number) =>
    count === 0
      ? 'No layers of its own.'
      : count <= names.length
        ? `Its layers: ${series(names)}.`
        : `${count} layers of its own, including ${series(names)}.`,
  viewPage: 'View its page',
  removedBefore: (date: string) =>
    `You removed it from this campaign on ${date}. Approving brings it back, and its whole history counts here again.`,
  approveTitle: 'Approve this request?',
  approveBody: (child: string, parent: string) =>
    `${child} becomes a layer of ${parent}: its whole history counts in ${possessive(parent)} totals, and it ends when ${parent} ends. Its organizers agreed to this when they asked, and only this campaign’s organizers can remove it.`,
  approveConfirm: 'Approve request',

  // ── Where the organizer lands: the Fundraisers list ──────────────────────────────────────────
  listCount: (count: number) => (count === 1 ? '1 request to join' : `${count} requests to join`),

  // ── The child's side: "Add a layer above" gains a second way ─────────────────────────────────
  aboveOthersDescription: 'It joins right away or asks first, as that campaign’s organizers allow.',
  outcomeJoin: (parent: string) => `${parent} lets other organizations’ campaigns join right away.`,
  outcomeAsk: (parent: string) =>
    `${possessive(parent)} organizers approve each campaign that asks to join.`,
  outcomeInvite: (parent: string) =>
    `${parent} adds campaigns only by invitation. Ask its organizers to invite this campaign.`,
  outcomeMembers: (parent: string, owner: string) =>
    `${parent} is open only to ${possessive(owner)} own people. Ask its organizers to invite this campaign.`,
  outcomeRemoved: (parent: string, date: string) =>
    `${possessive(parent)} organizers removed this campaign on ${date}, so it can only ask to join again.`,
  outcomeNoFit: (parent: string, kind: 'campaign' | 'list') =>
    kind === 'campaign'
      ? `This campaign raises for other charities than ${parent} requires, so it can’t join it.`
      : `This campaign raises for charities that aren’t on ${possessive(parent)} list, so it can’t join it.`,
  ifJoins: 'If this campaign joins:',
  ifApproved: 'If they approve:',
  consentJoin: 'Joining is your organization’s consent to all of this.',
  consentAsk: 'Asking is your organization’s consent to all of this, once they approve.',
  joinConfirm: 'Join campaign',
  askConfirm: 'Request to join',
  joinedToast: (parent: string) => `Joined ${parent}.`,
  askedToast: (parent: string) => `Asked to join ${parent}.`,

  // ── The child's tree while it waits, and after ───────────────────────────────────────────────
  pendingBadge: 'Pending',
  pendingLine: (asked: string, expires: string) => `Asked ${asked} · Expires ${expires}`,
  pendingNote: (parent: string) =>
    `This campaign becomes part of ${parent} if its organizers approve.`,
  withdraw: 'Withdraw request',
  withdrawTitle: 'Withdraw this request?',
  withdrawBody: (parent: string) =>
    `${possessive(parent)} organizers can no longer approve it. You can ask again later.`,
  withdrawnToast: 'Request withdrawn.',
  approvedNotice: (parent: string, date: string) =>
    `${possessive(parent)} organizers approved this campaign’s request on ${date}. It’s now part of ${parent}.`,
  declinedNotice: (parent: string, date: string) =>
    `${possessive(parent)} organizers declined this campaign’s request on ${date}. It can’t ask to join ${parent} again, but they can still invite it.`,
  expiredNotice: (parent: string, date: string) =>
    `This campaign’s request to join ${parent} expired on ${date}, so nothing changed. You can ask again.`,
  cancelledNotice: (parent: string) =>
    `${parent} ended, so this campaign’s request to join it was cancelled.`,
} as const;

/**
 * FCM-BE47's own sentences (`app/domain/campaign_join_requests.py` at backend origin/development
 * `988dc4e31f`; Jonathan approved them 2026-10-19 00:35 PDT), where the add dialog says the same fact
 * before any refusal. In the ruled words, typographic apostrophes included (the server writes
 * "isn't" with a straight one).
 */
export const REQUEST_SERVER_WORDS = {
  paused: (parent: string) => `${parent} isn’t accepting new campaigns right now.`,
  declined: (parent: string) =>
    `${parent}’s organizers declined this campaign’s request. It can’t ask to join ${parent} again, but they can still invite it.`,
} as const;
